import React, { useEffect, useState } from 'react';
import { getNewsList } from '../../services/AppService';
import { resolveMediaUrl } from '../../utility/media';
import closeButton from '../../assets/images/close.png';
import { useNavigate } from 'react-router-dom';

const ViewNews = () => {
    const navigate = useNavigate();
    const [newsList, setNewsList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        setLoading(true);
        const startTime = Date.now();

        try {
            const data = await getNewsList(0, 100);
            
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(1000 - elapsedTime, 0);

            // Ensure loader stays for at least 1 second
            setTimeout(() => {
                setNewsList(data?.content || []);
                setLoading(false);
            }, remainingTime);

        } catch (error) {
            console.error("Error fetching news:", error);
            setLoading(false);
        }
    };

    const filterByType = (type) => newsList.filter(item => 
        item.attachments?.[0]?.newsType === type
    );

    // --- Standard Spinner Component ---
    const Loader = () => (
        <div className="flex flex-col items-center justify-center py-40">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin mb-4"></div>
            <p className="text-blue-800 font-semibold tracking-wide">Fetching Latest Updates...</p>
        </div>
    );

    const NewsCard = ({ item }) => {
        const attachment = item.attachments?.[0];
        const mediaUrl = resolveMediaUrl(attachment?.fileLocation);

        return (
            <div className="bg-white rounded-lg shadow-md border overflow-hidden flex flex-col h-full hover:shadow-lg transition-shadow duration-300">
                <div className="p-2 bg-gray-50 border-b">
                    <h6 className="text-[13px] font-bold text-gray-800 truncate text-center" title={item.newsHeadline}>
                        {item.newsHeadline}
                    </h6>
                </div>
                <div className="flex-grow flex items-center justify-center bg-gray-100 p-1">
                    {attachment?.newsType === 'PICTURE' && (
                        <img 
                            src={mediaUrl} 
                            alt="news" 
                            className="w-full h-48 object-cover cursor-pointer rounded" 
                            onClick={() => window.open(mediaUrl, '_blank')}
                        />
                    )}
                    {attachment?.newsType === 'VIDEO' && (
                        <video controls className="w-full h-48 bg-black rounded">
                            <source src={mediaUrl} type="video/mp4" />
                        </video>
                    )}
                    {attachment?.newsType === 'VOICE' && (
                        <div className="w-full p-4 flex flex-col items-center">
                            <audio controls className="w-full h-10 shadow-sm">
                                <source src={mediaUrl} />
                            </audio>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const SectionHeader = ({ title }) => (
        <div className="w-full border-b-2 border-blue-600 mb-6 mt-8">
            <h5 className="bg-blue-600 text-white inline-block px-6 py-1 rounded-t-lg text-sm font-semibold uppercase tracking-wider">
                {title}
            </h5>
        </div>
    );

    return (
        <div className="bg-[#f4f7f6] min-h-screen">
            <div className="max-w-7xl mx-auto bg-white rounded shadow-xl border-t-4 border-blue-800 p-6 min-h-[70vh]">
                
                {/* Header Section */}
                <div className="flex justify-between items-center border-b pb-4 mb-2">
                    <div className="flex-grow text-center">
                        <h2 className="text-2xl font-bold text-blue-900 uppercase">Government News Portal</h2>
                    </div>
                    <img 
                        src={closeButton} 
                        alt="Close" 
                        className="w-7 h-7 cursor-pointer opacity-70 hover:opacity-100 transition-opacity" 
                        onClick={() => navigate(-1)} 
                    />
                </div>

                {loading ? (
                    <Loader />
                ) : (
                    <div className="space-y-10">
                        {/* Images Section */}
                        {filterByType('PICTURE').length > 0 && (
                            <section>
                                <SectionHeader title="Photo Gallery" />
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                    {filterByType('PICTURE').map(item => <NewsCard key={item.id} item={item} />)}
                                </div>
                            </section>
                        )}

                        {/* Audio Section */}
                        {filterByType('VOICE').length > 0 && (
                            <section>
                                <SectionHeader title="Audio Announcements" />
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {filterByType('VOICE').map(item => <NewsCard key={item.id} item={item} />)}
                                </div>
                            </section>
                        )}

                        {/* Video Section */}
                        {filterByType('VIDEO').length > 0 && (
                            <section>
                                <SectionHeader title="Video Coverage" />
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {filterByType('VIDEO').map(item => <NewsCard key={item.id} item={item} />)}
                                </div>
                            </section>
                        )}

                        {newsList.length === 0 && (
                            <div className="text-center py-20 text-gray-500 italic font-medium">
                                No official news updates are available at this time.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewNews;